// RoleManagement.jsx
import React, { useState, useEffect } from 'react';
import '../members/MembersPage.css';
import { buildApiUrl, getAuthHeaders } from '../../config/api';
import { useRestaurant } from '../../context/RestaurantContext';
import { notify, confirm } from '../../context/FeedbackContext';
import { FaEdit, FaTrash, FaPlus, FaSave, FaTimes, FaToggleOn, FaToggleOff, FaChevronRight, FaChevronDown, FaKey } from 'react-icons/fa';
import { getFilteredNavItemsWithIcons, getRouteFromItem } from '../../config/routes';

function RoleManagement({ onNavigate }) {
  const { selectedRestaurant } = useRestaurant() || {};
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [goToPage, setGoToPage] = useState('');

  // 基本信息编辑相关状态
  const [showBasicModal, setShowBasicModal] = useState(false);
  const [editingBasicRole, setEditingBasicRole] = useState(null);
  const [basicFormData, setBasicFormData] = useState({
    name: '',
    status: 1
  });

  // 权限编辑相关状态
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [editingPermissionRole, setEditingPermissionRole] = useState(null);
  const [permissionFormData, setPermissionFormData] = useState({
    permissions: []
  });

  // 权限树形列表
  const [permissionTree, setPermissionTree] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState(new Set());

  const itemsPerPage = 10;

  // 从PermissionManagement获取权限树形列表
  const fetchPermissions = async () => {
    setLoadingPermissions(true);
    try {
      const res = await fetch(
        buildApiUrl(`/api/permission/tree`),
        { headers: getAuthHeaders() }
      );
      const json = await res.json();
      if (json.code === 200 && json.data) {
        setPermissionTree(json.data);
        // 默认展开所有节点
        const allExpanded = new Set();
        const flattenTree = (tree) => {
          tree.forEach(item => {
            allExpanded.add(item.id);
            if (item.children && item.children.length) {
              flattenTree(item.children);
            }
          });
        };
        flattenTree(json.data);
        setExpandedKeys(allExpanded);
      } else {
        console.error('Failed to fetch permissions:', json.msg || json.message);
        setPermissionTree([]);
      }
    } catch (err) {
      console.error('Failed to fetch permissions', err);
      setPermissionTree([]);
    } finally {
      setLoadingPermissions(false);
    }
  };

  // 切换展开/收起
  const toggleExpand = (id) => {
    const newExpandedKeys = new Set(expandedKeys);
    if (newExpandedKeys.has(id)) {
      newExpandedKeys.delete(id);
    } else {
      newExpandedKeys.add(id);
    }
    setExpandedKeys(newExpandedKeys);
  };

  // 全部展开
  const expandAll = () => {
    const allExpanded = new Set();
    const flattenTree = (tree) => {
      tree.forEach(item => {
        allExpanded.add(item.id);
        if (item.children && item.children.length) {
          flattenTree(item.children);
        }
      });
    };
    flattenTree(permissionTree);
    setExpandedKeys(allExpanded);
  };

  // 全部收起
  const collapseAll = () => {
    setExpandedKeys(new Set());
  };

  const handleNavClick = (item) => {
    if (onNavigate) {
      onNavigate(getRouteFromItem(item));
    }
  };

  // 获取所有子节点的 reference（向下遍历）
  const getAllChildReferences = (node) => {
    const references = [];
    if (node.children && node.children.length > 0) {
      node.children.forEach(child => {
        references.push(child.reference);
        references.push(...getAllChildReferences(child));
      });
    }
    return references;
  };

  // 在树中查找指定reference的节点
  const findNodeByReference = (tree, ref) => {
    for (const item of tree) {
      if (item.reference === ref) {
        return item;
      }
      if (item.children && item.children.length > 0) {
        const found = findNodeByReference(item.children, ref);
        if (found) return found;
      }
    }
    return null;
  };

  // 获取某个节点的所有父节点reference（向上追溯）
  const getParentReferences = (tree, targetRef, parents = []) => {
    for (const item of tree) {
      if (item.reference === targetRef) {
        return parents;
      }
      if (item.children && item.children.length > 0) {
        const found = getParentReferences(item.children, targetRef, [...parents, item.reference]);
        if (found) return found;
      }
    }
    return null;
  };

  // 修改：使用新的接口获取角色列表（包含权限）
  const fetchRoles = async () => {
    // if (!selectedRestaurant?.reference) return;
    setLoading(true);
    try {
      const res = await fetch(
        buildApiUrl(`/api/userPermission/userRolePermList`),
        { headers: getAuthHeaders() }
      );
      const json = await res.json();
      if (json.code === 200 && json.data) {
        setRoles(json.data);
        setCurrentPage(1);
      } else {
        notify(json.code + ':' + json.msg, 'error')
        console.error('Failed to fetch roles:', json.message);
        setRoles([]);
      }
    } catch (err) {
      console.error('Failed to fetch roles', err);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, [selectedRestaurant]);

  const handleToggleStatus = async (role) => {
    const newStatus = role.status === 1 ? 0 : 1;
    const action = newStatus === 1 ? 'enable' : 'disable';

    if (await confirm(`Are you sure you want to ${action} role "${role.roleName}"?`)) {
      try {
        const res = await fetch(
          buildApiUrl(`/api/userPermission/userRoleUpdateStatus`),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders()
            },
            body: JSON.stringify({
              status: newStatus,
              reference: role.reference
            })
          }
        );
        const json = await res.json();
        if (json.code === 200) {
          fetchRoles();
          notify(newStatus === 1 ? 'Role enabled successfully!' : 'Role disabled successfully!', 'success');
        } else {
          notify(json.msg || json.message || `Failed to ${action} role`, 'error');
        }
      } catch (err) {
        console.error('Failed to update role status', err);
        notify(`Failed to ${action} role`, 'error');
      }
    }
  };

  const paginatedRoles = roles.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(roles.length / itemsPerPage);

  // 打开基本信息编辑模态框
  const handleOpenBasicModal = (role = null) => {
    if (role) {
      setEditingBasicRole(role);
      setBasicFormData({
        name: role.roleName,

        status: role.status !== undefined ? role.status : 1
      });
    } else {
      setEditingBasicRole(null);
      setBasicFormData({
        name: '',

        status: 1
      });
    }
    setShowBasicModal(true);
  };

  // 关闭基本信息编辑模态框
  const handleCloseBasicModal = () => {
    setShowBasicModal(false);
    setTimeout(() => {
      setEditingBasicRole(null);
      setBasicFormData({
        name: '',

        status: 1
      });
    }, 100);
  };

  // 打开权限编辑模态框 - 现在可以直接使用角色中的权限数据
  const handleOpenPermissionModal = (role) => {
    setEditingPermissionRole(role);
    // 直接使用角色中的权限数据（现在接口返回的数据中包含 permissions 字段）
    setPermissionFormData({
      permissions: role.permissions || []
    });
    setShowPermissionModal(true);
  };

  // 关闭权限编辑模态框
  const handleClosePermissionModal = () => {
    setShowPermissionModal(false);
    setTimeout(() => {
      setEditingPermissionRole(null);
      setPermissionFormData({ permissions: [] });
    }, 100);
  };

  const handlePermissionToggle = (permissionReference) => {
    // 找到当前节点
    const node = findNodeByReference(permissionTree, permissionReference);
    if (!node) return;

    setPermissionFormData(prev => {
      const isSelected = prev.permissions.includes(permissionReference);
      let newPermissions = [...prev.permissions];

      // 获取所有子节点引用
      const childReferences = node.children && node.children.length > 0
        ? getAllChildReferences(node)
        : [];

      // 获取所有父节点引用
      const parentReferences = getParentReferences(permissionTree, permissionReference) || [];

      if (isSelected) {
        // 取消选中：移除当前节点和所有子节点
        newPermissions = newPermissions.filter(p => p !== permissionReference);
        if (childReferences.length > 0) {
          newPermissions = newPermissions.filter(p => !childReferences.includes(p));
        }
      } else {
        // 选中：添加当前节点、所有子节点（向下）、所有父节点（向上）
        if (!newPermissions.includes(permissionReference)) {
          newPermissions.push(permissionReference);
        }

        // 添加所有子节点（级联向下）
        childReferences.forEach(ref => {
          if (!newPermissions.includes(ref)) {
            newPermissions.push(ref);
          }
        });

        // 添加所有父节点（级联向上）
        parentReferences.forEach(parentRef => {
          if (!newPermissions.includes(parentRef)) {
            newPermissions.push(parentRef);
          }
        });
      }

      return {
        ...prev,
        permissions: newPermissions
      };
    });
  };

  // 保存基本信息 - 优化后的逻辑
  const handleBasicSubmit = async (e) => {
    e.preventDefault();
    if (!basicFormData.name.trim()) {
      notify('Role name is required', 'error');
      return;
    }

    const isEditMode = editingBasicRole && editingBasicRole.reference;

    let rolePayload;

    if (isEditMode) {
      rolePayload = {
        roleName: basicFormData.name,

        status: basicFormData.status,
        reference: editingBasicRole.reference
      };
    } else {
      rolePayload = {
        roleName: basicFormData.name,

        status: basicFormData.status
      };
    }

    try {
      const res = await fetch(buildApiUrl(`/api/userPermission/saveOrUpdateRole`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(rolePayload)
      });

      const json = await res.json();
      if (json.code === 200) {
        await fetchRoles();
        handleCloseBasicModal();
        notify(isEditMode ? 'Role updated successfully!' : 'Role created successfully!', 'success');
      } else {
        notify(json.message || 'Failed to save role', 'error');
      }
    } catch (err) {
      console.error('Failed to save role', err);
      notify('Failed to save role', 'error');
    }
  };

  // 保存权限
  const handlePermissionSubmit = async (e) => {
    e.preventDefault();

    if (!editingPermissionRole) {
      notify('No role selected', 'error');
      return;
    }

    try {
      const roleId = editingPermissionRole.reference;

      // 步骤1：先删除旧权限
      if (editingPermissionRole.permissions && editingPermissionRole.permissions.length > 0) {
        try {
          const delRes = await fetch(
            buildApiUrl('/api/userPermission/del/batch'),
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
              },
              body: JSON.stringify({
                roleId: roleId,
                permissionId: editingPermissionRole.permissions
              })
            }
          );
          const delJson = await delRes.json();
          if (delJson.code !== 200) {
            console.warn('删除旧权限失败:', delJson.message);
          }
        } catch (delErr) {
          console.error('Failed to delete old permissions:', delErr);
        }
      }

      // 步骤2：分配新权限
      if (permissionFormData.permissions.length > 0) {
        try {
          const batchRes = await fetch(
            buildApiUrl('/api/userPermission/batch'),
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
              },
              body: JSON.stringify({
                roleId: roleId,
                permissionId: permissionFormData.permissions
              })
            }
          );
          const batchJson = await batchRes.json();
          if (batchJson.code === 200) {
            fetchRoles();
            handleClosePermissionModal();
            notify('Permissions updated successfully!', 'success');
          } else {
            notify(batchJson.message || 'Failed to update permissions', 'error');
          }
        } catch (batchErr) {
          console.error('Failed to assign permissions:', batchErr);
          notify('Failed to update permissions', 'error');
        }
      } else {
        // 如果没有选择任何权限
        fetchRoles();
        handleClosePermissionModal();
        notify('Permissions cleared successfully!', 'success');
      }
    } catch (err) {
      console.error('Failed to update permissions', err);
      notify('Failed to update permissions', 'error');
    }
  };

  const handleDelete = async (role) => {
    if (await confirm(`Are you sure you want to delete role "${role.roleName}"?`)) {
      try {
        const res = await fetch(
          buildApiUrl(`/api/userPermission/userRoleDelete`),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders()
            },
            body: JSON.stringify({ reference: role.reference })
          }
        );
        const json = await res.json();
        if (json.code === 200) {
          fetchRoles();
          notify('Role deleted successfully!', 'success');
        } else {
          notify(json.message || 'Failed to delete role', 'error');
        }
      } catch (err) {
        console.error('Failed to delete role', err);
        notify('Failed to delete role', 'error');
      }
    }
  };

  const renderPageButtons = () => {
    const buttons = [];
    buttons.push(
      <button key="prev" className={`pg-btn${currentPage === 1 ? ' disabled' : ''}`}
        onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}>Previous</button>
    );
    const start = Math.max(1, Math.min(currentPage - 1, totalPages - 2));
    const end = Math.min(totalPages, start + 2);
    if (start > 1) {
      buttons.push(<span key="dots-start" className="pg-dots">...</span>);
    }
    for (let i = start; i <= end; i++) {
      buttons.push(
        <button key={i} className={`pg-btn${currentPage === i ? ' active' : ''}`}
          onClick={() => setCurrentPage(i)}>{i}</button>
      );
    }
    if (end < totalPages) {
      buttons.push(<span key="dots-end" className="pg-dots">...</span>);
    }
    buttons.push(
      <button key="next" className={`pg-btn${currentPage === totalPages ? ' disabled' : ''}`}
        onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}>Next</button>
    );
    return buttons;
  };

  const showingFrom = roles.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const showingTo = Math.min(currentPage * itemsPerPage, roles.length);

  const getStatusStyle = (status) => {
    return {
      whiteSpace: 'nowrap',
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 'bold',
      background: status === 1 ? '#d4edda' : '#f8d7da',
      color: status === 1 ? '#155724' : '#721c24'
    };
  };

  // 获取权限类型名称
  const getTypeName = (type) => {
    const typeNames = { 1: 'Menu', 2: 'Button', 3: 'interface' };
    return typeNames[type] || 'Other';
  };

  // 递归渲染权限树（带复选框）
  const renderPermissionTree = (items, level = 0) => {
    let rows = [];
    for (const item of items) {
      const hasChildren = item.children && item.children.length > 0;
      const isExpanded = expandedKeys.has(item.id);
      const paddingLeft = 20 + level * 20;

      // 检查当前节点是否被选中
      const isSelected = permissionFormData.permissions.includes(item.reference);

      // 检查所有子节点是否都被选中（用于显示半选状态）
      const getAllChildReferencesForCheck = (node) => {
        const references = [];
        if (node.children && node.children.length > 0) {
          node.children.forEach(child => {
            references.push(child.reference);
            references.push(...getAllChildReferencesForCheck(child));
          });
        }
        return references;
      };

      const childReferences = hasChildren ? getAllChildReferencesForCheck(item) : [];
      const allChildrenSelected = hasChildren && childReferences.length > 0 &&
        childReferences.every(ref => permissionFormData.permissions.includes(ref));
      const someChildrenSelected = hasChildren && childReferences.length > 0 &&
        childReferences.some(ref => permissionFormData.permissions.includes(ref));

      rows.push(
        <tr key={item.id} style={{ opacity: item.status === 0 ? 0.7 : 1 }}>
          <td style={{ textAlign: 'left', paddingLeft: `${paddingLeft}px`, display: 'flex', alignItems: 'center' }}>
            {/* 1. 复选框 - 放在最前面 */}
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => handlePermissionToggle(item.reference)}
              disabled={item.status === 0}
              style={{
                marginRight: '8px',
                cursor: item.status === 0 ? 'not-allowed' : 'pointer',
                verticalAlign: 'middle',
                width: '16px',
                height: '16px'
              }}
              title={hasChildren ? "选中将自动选中所有子权限和父权限" : "选中将自动选中所有父权限"}
            />

            {/* 2. 折叠图标 */}
            {hasChildren && (
              <span
                onClick={() => toggleExpand(item.id)}
                style={{
                  cursor: 'pointer',
                  marginRight: '8px',
                  display: 'inline-block',
                  width: '16px',
                  color: '#666',
                  verticalAlign: 'middle'
                }}
              >
                {isExpanded ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
              </span>
            )}
            {!hasChildren && (
              <span style={{
                display: 'inline-block',
                width: '24px',
                marginRight: '8px',
                verticalAlign: 'middle'
              }} />
            )}

            {/* 3. 权限名称和其他信息 */}
            <strong style={{ verticalAlign: 'middle' }}>{item.permissionName}</strong>
            {item.type === 2 && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#888' }}>(Button)</span>}
            {item.type === 3 && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#888' }}>(interface)</span>}
            {item.status === 0 && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#999' }}>(Discontinue)</span>}

            {/* 显示子节点选中状态指示器 */}
            {hasChildren && someChildrenSelected && !allChildrenSelected && (
              <span style={{
                marginLeft: '8px',
                fontSize: '10px',
                color: '#17a2b8',
                background: '#e3f2fd',
                padding: '1px 4px',
                borderRadius: '3px'
              }}>
                Partially selected
              </span>
            )}
           </td>
          <td style={{ textAlign: 'left' }}>
            <code style={{ background: '#f4f6fa', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' }}>
              {item.permissionCode}
            </code>
           </td>
          <td style={{ textAlign: 'center' }}>
            <span style={{
              background: item.type === 1 ? '#e3f2fd' : item.type === 2 ? '#f3e5f5' : '#fff3e0',
              color: item.type === 1 ? '#1976d2' : item.type === 2 ? '#7b1fa2' : '#e65100',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '11px'
            }}>
              {item.path}
            </span>
           </td>
          <td style={{ textAlign: 'center' }}>
            <span style={{
              background: item.type === 1 ? '#e3f2fd' : item.type === 2 ? '#f3e5f5' : '#fff3e0',
              color: item.type === 1 ? '#1976d2' : item.type === 2 ? '#7b1fa2' : '#e65100',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '11px'
            }}>
              {getTypeName(item.type)}
            </span>
           </td>
        </tr>
      );

      // 如果展开且有子节点，递归渲染子节点
      if (hasChildren && isExpanded) {
        rows = rows.concat(renderPermissionTree(item.children, level + 1));
      }
    }
    return rows;
  };

  const { items: navItems, icons: navIcons } = getFilteredNavItemsWithIcons();

  return (
    <div className="memberspage-container">
      <nav className="sidebar">
        <div className="sidebar-title">Restaurant Management</div>
        <ul>
          {navItems.map(item => (
            <li key={item} className={item === 'Role Management' ? 'active' : ''}
              onClick={() => handleNavClick(item)}>
              <span className="nav-icon">{navIcons[item]}</span> {item}
            </li>
          ))}
        </ul>
      </nav>

      <main className="members-content">
        <div className="admin-breadcrumb">
          <span className="breadcrumb-link" onClick={() => handleNavClick('Home')}>Home</span>
          <span className="breadcrumb-sep">›</span>
          <span className="breadcrumb-current">Role Management</span>
        </div>
        <h1 className="admin-page-title">Role Management</h1>
        <p className="admin-page-subtitle">Manage user roles and permissions. Control what each role can access.</p>

        <div className="user-list-card">
          <div className="user-list-header">
            <h2 className="user-list-title">Role List</h2>
            <div className="user-list-actions">
              <HasPermi permission={['roles:saveOrUpdateRole']}>
                <button className="add-restaurant-btn" onClick={() => handleOpenBasicModal()}>
                  <FaPlus /> Create Role
                </button>
              </HasPermi>
            </div>
          </div>

          <table className="restaurant-table role-management-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>ROLE NAME</th>
                <th style={{ textAlign: 'center' }}>STATUS</th>
                <th style={{ textAlign: 'center' }}>OPERATION</th>
              </tr>
            </thead>
            <tbody className="role-table-body">
              {loading ? (
                <tr><td colSpan="3" style={{ textAlign: 'center', padding: '40px' }}>Loading...</td></tr>
              ) : paginatedRoles.length === 0 ? (
                <tr><td colSpan="3" style={{ textAlign: 'center', padding: '40px' }}>No roles found. Create your first role!</td></tr>
              ) : (
                paginatedRoles.map(role => (
                  <tr key={role.id || role.reference} style={{ opacity: role.status === 0 ? 0.7 : 1 }}>
                    <td style={{ textAlign: 'left' }}>
                      <strong>{role.roleName}</strong>
                      {/* 现在可以直接使用 role.permissions，因为接口返回的数据中包含这个字段 */}
                      {role.permissions && role.permissions.length > 0 && (
                        <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                          Number of permissions: {role.permissions.length}
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={getStatusStyle(role.status)}>
                        {role.status === 1 ? 'enable' : 'disable'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {/* 编辑基本信息按钮 */}
                      <HasPermi permission={['roles:saveOrUpdateRole']}>
                        <button
                          className="edit-btn"
                          onClick={() => handleOpenBasicModal(role)}
                          title="Edit Basic Information"
                        >
                          <FaEdit />
                        </button>
                      </HasPermi>

                      {/* 编辑权限按钮 */}
                      <HasPermi permission={['roles:batch','roles:delBatch']} any>
                        <button
                          className="edit-btn"
                          onClick={() => handleOpenPermissionModal(role)}
                          title="Edit Permissions"
                          style={{ marginLeft: '8px', background: '#17a2b8' }}
                        >
                          <FaKey />
                        </button>
                      </HasPermi>
                      <HasPermi permission={['roles:userRoleUpdateStatus']}>
                        <button
                          className="status-toggle-btn"
                          onClick={() => handleToggleStatus(role)}
                          style={{
                            marginLeft: '8px',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1.2rem',
                            color: role.status === 1 ? '#28a745' : '#6c757d',
                            verticalAlign: 'middle'
                          }}
                          title={role.status === 1 ? 'Click to disable' : 'Click to enable'}
                        >
                          {role.status === 1 ? <FaToggleOn /> : <FaToggleOff />}
                        </button>
                      </HasPermi>
                      <HasPermi permission={['roles:userRoleDelete']}>
                        <button
                          className="delete-btn"
                          onClick={() => handleDelete(role)}
                          style={{ marginLeft: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c' }}
                        >
                          <FaTrash />
                        </button>
                      </HasPermi>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {roles.length > 0 && (
            <div className="pagination-row">
              <span className="pagination-info">Showing {showingFrom} to {showingTo} of {roles.length} results</span>
              <div className="pagination-buttons">{renderPageButtons()}</div>
              <div className="pagination-goto">
                <span>Go to page</span>
                <input className="pg-goto-input" type="number" min="1" max={totalPages} value={goToPage}
                  onChange={e => setGoToPage(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { const p = parseInt(goToPage); if (p >= 1 && p <= totalPages) { setCurrentPage(p); setGoToPage(''); } } }}
                  placeholder="" />
                <button className="pg-btn" onClick={() => { const p = parseInt(goToPage); if (p >= 1 && p <= totalPages) { setCurrentPage(p); setGoToPage(''); } }}>Go</button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* 基本信息编辑模态框 */}
      {showBasicModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="admin-modal-header">
              <button className="admin-modal-close" onClick={handleCloseBasicModal}>×</button>
              <h2 className="admin-modal-title">
                {editingBasicRole && editingBasicRole.reference ? 'Edit Role Information' : 'Create New Role'}
              </h2>
              <p className="admin-modal-subtitle">
                {editingBasicRole && editingBasicRole.reference ? 'Update role basic information.' : 'Fill in the details to create a new role.'}
              </p>
            </div>
            <div className="admin-modal-body">
              <div className="form-field">
                <label>Role Name <span className="required">*</span></label>
                <input
                  type="text"
                  value={basicFormData.name}
                  onChange={(e) => setBasicFormData({ ...basicFormData, name: e.target.value })}
                  placeholder="e.g., Admin, Manager, Staff"
                />
              </div>

              <div className="form-field">
                <label>Status</label>
                <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="status"
                      value={1}
                      checked={basicFormData.status === 1}
                      onChange={() => setBasicFormData({ ...basicFormData, status: 1 })}
                    />
                    <span style={getStatusStyle(1)}>enable</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="status"
                      value={0}
                      checked={basicFormData.status === 0}
                      onChange={() => setBasicFormData({ ...basicFormData, status: 0 })}
                    />
                    <span style={getStatusStyle(0)}>disable</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="cancel-btn" onClick={handleCloseBasicModal}>Cancel</button>
              <button className="submit-btn" onClick={handleBasicSubmit}>
                <FaSave /> {editingBasicRole && editingBasicRole.reference ? 'Update Role' : 'Create Role'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 权限编辑模态框 */}
      {showPermissionModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal" onClick={e => e.stopPropagation()} style={{  width: '60%' }}>
            <div className="admin-modal-header">
              <button className="admin-modal-close" onClick={handleClosePermissionModal}>×</button>
              <h2 className="admin-modal-title">
                Edit Role Permissions - {editingPermissionRole?.roleName}
              </h2>
              <p className="admin-modal-subtitle">
                Update permissions for this role.
              </p>
            </div>
            <div className="admin-modal-body">
              <div className="form-field">
                <label>Permissions</label>
                {loadingPermissions ? (
                  <div style={{ padding: '20px', textAlign: 'center' }}>Loading permissions...</div>
                ) : permissionTree.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
                    No permissions found. Please create permissions in Permission Management first.
                  </div>
                ) : (
                  <>
                    {/* 树形结构权限选择 */}
                    <div style={{ marginBottom: '10px', display: 'flex' }}>
                      <button className="add-restaurant-btn" onClick={expandAll} style={{ marginRight: '8px', padding: '4px 8px', fontSize: '12px' }}>
                        Expand All
                      </button>
                      <button className="add-restaurant-btn" onClick={collapseAll} style={{ padding: '4px 8px', fontSize: '12px', background: '#6c757d' }}>
                        Collapse All
                      </button>
                    </div>

                    <table className="permission-tree-table" style={{ width: '100%', border: '1px solid #ddd', borderRadius: '6px' }}>
                      <thead>
                        <tr style={{ background: '#f8f9fa' }}>
                          <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #ddd' }}>Permission Name</th>
                          <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #ddd' }}>Permission Code</th>
                          <th style={{ textAlign: 'center', padding: '10px', borderBottom: '1px solid #ddd' }}>Url</th>
                          <th style={{ textAlign: 'center', padding: '10px', borderBottom: '1px solid #ddd' }}>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {renderPermissionTree(permissionTree)}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
              {/* 显示已选权限数量 */}
              {permissionFormData.permissions.length > 0 && (
                <div style={{
                  padding: '10px',
                  background: '#f8f9fa',
                  borderRadius: '4px',
                  marginTop: '10px',
                  fontSize: '12px',
                  color: '#666'
                }}>
                  Selected {permissionFormData.permissions.length} permissions
                </div>
              )}
            </div>
            <div className="admin-modal-footer">
              <button className="cancel-btn" onClick={handleClosePermissionModal}>Cancel</button>
              <button className="submit-btn" onClick={handlePermissionSubmit}>
                <FaSave /> Update Permissions
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RoleManagement;